
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Building, FileCheck, LineChart, Loader2, Search, AlertTriangle } from 'lucide-react';
import UrlInput from '@/components/report/UrlInput';
import AnimatedButton from '@/components/AnimatedButton';
import FeatureCard from '@/components/FeatureCard';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import HeroAnalysisSample from '@/components/report/HeroAnalysisSample';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import LocationIndicator from '@/components/LocationIndicator';

const AnalisarImovel = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState<{
    step: number;
    message: string;
  }>({
    step: 0,
    message: ''
  });
  const [error, setError] = useState<string | null>(null);
  
  const handleAnalysisComplete = (analysis: any) => {
    // This will be called when the analysis is complete
    // Close the dialog after a short delay
    setTimeout(() => {
      setIsDialogOpen(false);
      setError(null);
    }, 1500);
  };
  
  const handleLoadingStateChange = (isLoading: boolean, step: number, message: string) => {
    console.log("Loading state changed:", isLoading, step, message);
    if (isLoading) {
      setIsDialogOpen(true);
      setError(null);
      setAnalysisStatus({
        step,
        message
      });
    } else {
      // When loading is finished, close the dialog after a brief delay
      setTimeout(() => {
        setIsDialogOpen(false);
      }, 1000);
    }
  };

  return <div className="flex flex-col min-h-screen pt-16 py-0">
      {/* Main content */}
      <div className="flex-grow">
        {/* Hero Section */}
        <section className="relative py-12 overflow-hidden bg-gradient-to-b from-white to-blue-50 md:py-[40px]">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5
            }} className="flex flex-col space-y-6">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-blue-dark leading-tight">Análise Inteligente de Imóveis</h1>
                <p className="text-base md:text-lg text-text-medium max-w-xl">
                  Faça uma análise completa do imóvel que você deseja em instantes. 
                  Basta colar o link do anúncio abaixo.
                </p>
                <LocationIndicator />
                <div className="flex flex-col space-y-6">
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="bg-white border-blue-medium text-blue-medium">
                      ZapImóveis
                    </Badge>
                    <Badge variant="outline" className="bg-white border-blue-medium text-blue-medium">
                      Chaves na Mão
                    </Badge>
                    <Badge variant="outline" className="bg-white border-blue-medium text-blue-medium">
                      InfoImóveis
                    </Badge>
                  </div>
                  <UrlInput className="shadow-lg" onAnalysisComplete={handleAnalysisComplete} onLoadingStateChange={handleLoadingStateChange} />
                </div>
              </motion.div>
              
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="hidden md:flex justify-center">
                <HeroAnalysisSample />
              </motion.div>
            </div>
          </div>
        </section>
        
        {/* How it Works Section */}
        <section className="py-12 md:py-16 bg-white">
          <div className="container mx-auto px-4">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} viewport={{
            once: true
          }} className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-blue-dark mb-4">Como Funciona</h2>
              <p className="text-text-medium max-w-2xl mx-auto">
                Vamos comparar o imóvel com a nossa base de dados de imóveis semelhantes e te entregar uma análise completa. Você ainda poderá pedir um toque final usando nossa Inteligência Artificial nas análises.
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-6 mt-10">
              <FeatureCard icon={<Search className="h-6 w-6 text-blue-medium" />} title="Cole o Link do Imóvel" description="Basta inserir o link do anúncio do imóvel à venda no InfoImoveis e receber sua análise." delay={0} />
              <FeatureCard icon={<Loader2 className="h-6 w-6 text-blue-medium" />} title="Análise Automática" description="Nosso sistema analisa automaticamente os dados do imóvel, compara com o mercado e avalia a qualidade do investimento." delay={1} />
              <FeatureCard icon={<FileCheck className="h-6 w-6 text-blue-medium" />} title="Relatório Completo" description="Receba um relatório detalhado com comparativos de mercado, análise de preço por m² e imóveis semelhantes." delay={2} />
            </div>
          </div>
        </section>
      </div>
      
      {/* Loading Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle>Status da Análise</DialogTitle>
          <DialogDescription className="sr-only">Acompanhe o progresso da análise do imóvel</DialogDescription>
          <div className="py-6">
            {error ? <div className="flex flex-col items-center">
                <AlertTriangle size={40} className="text-red-500 mb-3" />
                <h3 className="text-lg font-semibold text-center mb-2">Erro na Análise</h3>
                <p className="text-red-600 text-center mb-4">{error}</p>
              </div> : <>
                <div className="flex space-x-2 mb-3 justify-center">
                  <div className="w-3 h-3 bg-blue-medium rounded-full animate-bounce"></div>
                  <div className="w-3 h-3 bg-blue-medium rounded-full animate-bounce delay-75"></div>
                  <div className="w-3 h-3 bg-blue-medium rounded-full animate-bounce delay-150"></div>
                </div>
                <h3 className="text-lg font-semibold text-center mb-2">Analisando Imóvel</h3>
                <p className="text-gray-600 text-center mb-4">
                  Estamos processando os dados do imóvel. Isto pode levar alguns instantes.
                </p>
                <Progress value={Math.round(analysisStatus.step / 3 * 100)} className="h-2 mb-4" />
                <p className="text-xs text-gray-500 text-center">
                  Etapa {analysisStatus.step} de 3: {analysisStatus.message || "Iniciando análise..."}
                </p>
              </>}
          </div>
        </DialogContent>
      </Dialog>
    </div>;
};
export default AnalisarImovel;
